<script setup lang="ts">
import Loader from '@/Components/Loader.vue';
import Modal from '@/Components/Modal.vue';
import RulesetTable from '@/Components/RulesetTable.vue';
import { roundsCount } from '@/utils';
import { computed, onBeforeUnmount, onMounted, reactive, ref, toValue, useTemplateRef, watch } from 'vue';
import MessageBox from '@/Components/MessageBox.vue';
import Instructions from '@/Components/Instructions.vue';
import OnboardingFlow from '@/Components/OnboardingFlow.vue';
import { useParticipantStore } from '@/Stores/ParticipantStore';
import { usePage } from '@inertiajs/vue3';
import { useRouter } from 'vue-router';
import { Round, rulesetLabel, studyRulesets } from '@/objects';
import SessionView from '@/Components/SessionView.vue';
import { useCopy, renderCopy, blockVisible } from '@/copy';
import { useSessionStore } from '@/Stores/AppStore';
import SupportButton from '@/Components/SupportButton.vue';
import LogoutBtn from '@/Components/LogoutBtn.vue';

const router = useRouter()
const page = usePage()
const config = page.props.experimentConfigData
const copy = useCopy()
const sideNote = copy('play_side_note')

const pStore = useParticipantStore();
const sessionStore = useSessionStore()

// The instructions for the phase currently being played, reachable at any time from the
// "Istruzioni" button.
const instructionsShown = ref(false)
const instructionsPart = computed(() => pStore.currentPhase?.idx ?? 1)

// True once the participant has made at least one choice in the current phase. Used to decide
// whether we're still "at the start" (and should surface the intro instructions).
const phaseStarted = computed(() =>
    (pStore.currentPhase?.rounds ?? []).some((r) => r.choices[0])
)

// Login-time onboarding: a gated instructions-and-comprehension flow shown once before the
// session starts (phase 1, before the first action).
// "Already seen" lives on the participant record server-side (see completeOnboarding)
const onboardingParts = computed(() =>
    ((config['onboardingPart1'] ?? []) as any[])
        .filter((block) => blockVisible(block, pStore.state.hasChat))
)
const showOnboarding = computed(() =>
    onboardingParts.value.length > 0
    && pStore.currentPhase?.idx === 1
    && !phaseStarted.value
    && !pStore.state.onboardingCompleted
)

// The two tables a control question can be asked about (see OnboardingFlow's `rulesets`).
const onboardingRulesets = studyRulesets(page.props.chatSessionData as Record<string, any>)

// The table the group's vote landed on, named the way the Part 2 instructions and the vote
// screen name it (ORIGINALE = the Part 1 table, ALTERNATIVA = the other one).
const wonRulesetLabel = computed(() =>
    rulesetLabel(pStore.state.phase2.ruleset?.id, pStore.state.phase1.ruleset?.id)
)

const nextMessage = ref("")
const sessionView = useTemplateRef('session-view')
const sideContent = useTemplateRef('side-content')
const sideMobile = useTemplateRef('side-mobile')
const sideDesktop = useTemplateRef('side-desktop')
const sideShown = ref(false)
const sideIsMobile = ref(false)

const endModalShown = ref(false)
// Once the phase has played out (both pair members acted in every round, so the last
// result is on screen), we surface a Continue CTA instead of teleporting onward, giving
// the participant a beat to read their final result. Derived from the store's rounds, not
// set by whoever noticed the last choice land: the CTA then can't be missed however the
// final result arrived (own POST, opponent's ActionSent, reconciliation). voteReady tracks
// whether the server has already signalled the phase-1 vote can start, so Continue can
// skip the wait modal.
const continued = ref(false)
const awaitingContinue = computed(() => {
    const phase = pStore.currentPhase
    return !continued.value && !!phase && !phase.currentRound && phase.rounds.length >= roundsCount()
})
const voteReady = ref(false)

const REPLY_WAITING = "Il tuo assistente ti risponderà a breve"
const REPLY_ERROR = "Ops! Qualcosa è andato storto. Riprova più tardi."
const replyStatus = reactive({
    text: "",
    ok: true
})

const timer = ref(0)
let timerInterval = ref(null)

const timerString = computed(() => {
    if (!isTimerRunning.value) {
        return "00:00"
    }
    const t = Math.floor(timer.value)
    const m = Math.floor(t / 60)
    const s = timer.value % 60
    
    return [m, s].
        map((v) => String(v).padStart(2, "0")).
        join(":")
})
const isTimerRunning = computed(() => {
    return timer.value > 0
})
const currentRound = computed(() => {
    return pStore.currentRound
})

async function addMessage() {
    if (!nextMessage.value) {
        return
    }

    const msg = toValue(nextMessage)
    nextMessage.value = ""
    replyStatus.text = REPLY_WAITING
    replyStatus.ok = true
    timer.value = 0

    const res = await pStore.sendMessage(msg)

    replyStatus.ok = res.ok
    if (!replyStatus.ok) {
        replyStatus.text = REPLY_ERROR
        // The message never went through, so hand the text back for an easy retry.
        nextMessage.value = msg
    }

    console.log("message sent")
}

async function addChoice(choice: string) {
    nextMessage.value = ""

    await pStore.sendAction(choice)

    console.log("choice sent")

    advance()

    sessionView.value.scrollToLast()
}

// Advance past every fully-answered round, opening the next one as needed. Driven purely
// by round completeness, so it is idempotent and safe to call from both the local action
// and the opponent's ActionSent, in any order. Once every round has been played out there
// is nothing to open: the Continue CTA (awaitingContinue) follows from the store on its own.
function advance() {
    const phase = pStore.currentPhase
    if (!phase) {
        return
    }

    // Open the next round while the current one is done and rounds still remain. We stop
    // exactly at ROUNDS_PER_PHASE so no empty trailing round is ever left dangling.
    while (!phase.currentRound && phase.rounds.length < roundsCount()) {
        phase.addRound(new Round(phase.rounds.length, [], pStore.state.hasChat))
    }
}

// Leave the finished board behind once the participant clicks Continue. Phase 1 leads to
// the inter-phase vote (immediately if the server already signalled it, otherwise via the
// wait modal); phase 2 leads to the consultant feedback (chat treatments) or straight to
// the questionnaire.
function proceed() {
    if (!awaitingContinue.value) {
        return
    }
    continued.value = true

    const phase = pStore.currentPhase
    if (!phase) {
        return
    }

    if (phase.idx === 1) {
        if (voteReady.value) {
            pStore.state.phase1.completed = true
            router.push({ name: 'PartVote', replace: true })
        }
        else {
            preVote()
        }
    }
    else {
        // Mark phase 2 done so the downstream route guards let us through instead of
        // bouncing us back to a finished board.
        pStore.state.phase2.completed = true

        if (pStore.state.hasChat && !pStore.state.phase2.evaluated) {
            router.push({ name: 'PartFeedback', replace: true })
        }
        else {
            router.push({ name: 'PartQEnd', replace: true })
        }
    }
}

function preVote() {
    endModalShown.value = true
    sideShown.value = false
}

function startTimer() {
    if (isTimerRunning.value) {
        return
    }

    timer.value = config["preChoiceTimer"]
    timerInterval.value = setInterval(() => { 
        if (sessionStore.paused) {
            return
        }
        
        if (timer.value <= 0) {
            clearInterval(timerInterval.value)
            return
        }
        
        timer.value -= 1
    }, 1000)
}

onMounted(() => {
    // fake-trigger the resize event to set up mobile-only elements
    // pass null since it expects an event and vscode complains
    window.onresize(null)

    // Reconcile on mount: if we land here with the phase already played out, advance()
    // routes us onward (vote / questionnaire) instead of stranding us on a dead board.
    advance()

    if (awaitingContinue.value) {
        // If we load the page and the Continue CTA is already active, assumed it was clicked before reloading
        proceed()
    }

    if (currentRound.value.awaitingReply) {
        replyStatus.text = REPLY_WAITING
    }

    pStore.onAction = (choice) => advance()
    // However the current round gets completed (own POST, opponent's ActionSent or a
    // reconciled snapshot), open the next one: progression follows the store, not the
    // callback that happened to notice.
    watch(() => pStore.currentPhase?.currentRound, (round) => {
        if (!round) {
            advance()
        }
    })
    pStore.onReply = (reply) => startTimer()
    pStore.onVoteStart = () => {
        voteReady.value = true

        // Only auto-advance participants who have already clicked Continue and are
        // sitting in the "wait for everyone" modal. Anyone still reviewing their final
        // result keeps the Continue button and advances on their click.
        if (endModalShown.value) {
            pStore.state.phase1.completed = true
            router.push({name: 'PartVote', replace: true})
        }
    }
})

window.onresize = () => {
    //FIXME: remove that ugly hardcoded number
    const newParent = (window.innerWidth <= 767) ? sideMobile.value : sideDesktop.value
    if (newParent === sideContent.value.parentElement) {
        return
    }

    sideIsMobile.value = (newParent === sideMobile.value)
    if (!sideIsMobile.value) {
        sideShown.value = false
    }

    newParent.appendChild(sideContent.value)
}

onBeforeUnmount(() => {
    window.onresize = null

    if (timerInterval.value) {
        clearInterval(timerInterval.value)
    }
})

</script>

<template>
    <Modal v-model="sideShown" class="h-full w-full bg-transparent" :close-outside="true">
        <div class="col gap-4 h-full side-container sm:w-[65%] w-full">
            <button @click="sideShown = false" class="btn-primary btn-small">
                X
            </button>
            <div ref="side-mobile" class="flex-1 min-h-0"></div>
        </div>
    </Modal>
    <template v-if="!sessionStore.paused">
        <Modal v-model="endModalShown" title="Prima parte conclusa!">
            Attendi che abbiano terminato tutti i partecipanti prima di continuare alla prossima fase
        </Modal>
        <Instructions v-model="instructionsShown" :part="instructionsPart" :has-chat="pStore.state.hasChat" :chat-type="pStore.state.chatType" />
    </template>
    <OnboardingFlow v-if="showOnboarding" :parts="onboardingParts" :chat-type="pStore.state.chatType"
        :rulesets="onboardingRulesets" @done="pStore.completeOnboarding()" />
    <!-- The mobile side-menu button lives in the app topbar, next to the countdown/Esci. -->
    <Teleport v-if="sideIsMobile" to="#topbar-left">
        <button @click="sideShown = true" class="btn-primary btn-small">
            ?
        </button>
    </Teleport>
    <div class="flex w-full h-full">
        <!-- Desktop sidebar spans the full viewport height, over the topbar (which only
             concerns the chat area): fixed to the left edge, above the bar's z-index. -->
        <div ref="side-desktop" class="max-md:hidden side-container md:fixed md:left-0 md:top-0 md:h-screen md:w-75 xl:w-105 md:z-30 md:overflow-y-auto">
            <div ref="side-content" class="col gap-3 h-full w-full">
                <!-- Table and side note share the scroll area so the actions below stay pinned
                     to the bottom however long the (researcher-editable) note is. -->
                <div class="col gap-3 flex-1 min-h-0 overflow-y-auto">
                    <RulesetTable v-if="pStore.currentPhase?.ruleset" :ruleset="pStore.currentPhase.ruleset" class="w-full shrink-0"/>
                    <div v-if="sideNote" class="copy-html text-sm" v-html="renderCopy(sideNote)"></div>
                </div>
                <div class="col gap-2 shrink-0">
                    <button @click="instructionsShown = true" class="btn-primary px-4! py-2!">
                        Istruzioni
                    </button>
                    <SupportButton variant="secondary" class="justify-center"></SupportButton>
                    <div class="row items-center gap-3 border-t-2 border-pink-800 pt-3 mt-1">
                        <div class="col flex-1 min-w-0">
                            <span class="text-xs text-gray-dark leading-none">Partecipante</span>
                            <span class="font-semibold truncate" :title="sessionStore.state.userLabel">{{ sessionStore.state.userLabel }}</span>
                        </div>
                        <LogoutBtn />
                    </div>
                </div>
            </div>
        </div>
        <div class="flex-1 min-w-0 col p-3 md:p-5 md:ml-75 xl:ml-105 gap-4">
            <SessionView ref="session-view" :session="pStore.state">
                <template #vote="{ item }">
                    <div class="col w-full items-center gap-3">
                        <div class="row bg-(--bg-color) items-center sticky top-0 w-full gap-2">
                            <span class="bg-pink-900 text-white text-xs text-center px-2 py-1 rounded-sm">
                                Votazione
                            </span>
                            <hr class="border flex-1 border-gray-900">
                        </div>
                        <div class="col bg-white rounded-md gap-3 p-3 w-full lg:w-175 text-center">
                            <p class="text-center text-lg font-bold">
                                Tabella per la fase successiva
                            </p>
                            <div class="row justify-center items-start gap-6 flex-wrap">
                                <div class="col items-center gap-2">
                                    <!-- Named ORIGINALE / ALTERNATIVA as in the Part 2 instructions and
                                         in the vote, so the table the group landed on is recognisable
                                         without re-reading the numbers. -->
                                    <div class="col items-center">
                                        <span class="text-sm font-bold">Scelta dal tuo gruppo</span>
                                        <span v-if="wonRulesetLabel" class="text-sm">TABELLA {{ wonRulesetLabel }}</span>
                                    </div>
                                    <RulesetTable v-if="item.won" :ruleset="item.won"/>
                                    <span v-else class="text-sm">—</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template #waiting="{ item }">
                    <div v-if="!item.historic && (item.round.awaitingReply || !replyStatus.ok)" 
                    class="text-sm text-white rounded-sm px-4 py-1 row items-center"
                    :class="replyStatus.ok ? 'bg-pink-900' : 'bg-error'">
                        <span>{{ replyStatus.text }}</span> 
                        <Loader :loading="true" class="w-12.5"/>
                    </div>
                </template>
                <template #choices="{ item }">
                    <div class="col bg-white rounded-md gap-2 p-3 w-full lg:w-175 text-center">
                        <p class="text-center text-lg font-bold">
                            Round {{ item.number+1 }}
                        </p>
                        <div class="grid grid-cols-2 grid-rows-[auto_auto] grid-flow-col text-md gap-x-2 gap-y-2 min-h-20">
                            <div class="choice">
                                <span>Tu hai scelto {{ item.round.choices[0] }}</span>
                            </div>
                            <div class="score">
                                Guadagno: {{ !item.round.awaitingChoice ? item.round.scores(item.ruleset)[0] : '???' }} punti
                            </div>
                            <!-- Until the other player acts, their half of the round is a labelled
                                 wait. -->
                            <div v-if="item.round.awaitingChoice"
                                class="row-span-2 col items-center justify-center text-sm text-gray-dark">
                                <span>Attendi la scelta dell'altro</span>
                                <Loader :loading="true" class="w-12.5"/>
                            </div>
                            <template v-else>
                                <div class="choice">
                                    <span class="my-auto">L'altro ha scelto {{ item.round.choices[1] }}</span>
                                </div>
                                <div class="score">
                                    Guadagno: {{ item.round.scores(item.ruleset)[1] }} punti
                                </div>
                            </template>
                        </div>
                    </div>
                </template>
            </SessionView>
            <div v-if="!endModalShown" class="col gap-3">
                <!-- Phase played out: take over the input area with a Continue CTA so the
                     participant reviews their final result before being moved onward. -->
                <button v-if="awaitingContinue" @click="proceed" data-testid="continue"
                    class="btn-primary continue-cta min-h-20 text-lg font-bold">
                    Continua
                </button>
                <template v-else>
                    <div class="min-h-20 flex-1 flex gap-2 relative">
                        <button v-for="choice in ['A','B']" @click="addChoice(choice)"
                            class="btn-primary flex-1" :data-testid="`choice-${choice}`"
                            :disabled="!currentRound || !currentRound.canChoose || isTimerRunning"
                        >
                            Scelta {{ choice }}
                        </button>
                        <div v-if="pStore.state.hasChat && timer" class="absolute size-full row items-center justify-center">
                            <div class="rounded-full bg-blue font-mono text-white p-2">
                                {{ timerString }}
                            </div>
                        </div>
                    </div>
                    <MessageBox v-if="pStore.state.hasChat"
                        v-model="nextMessage"
                        @send="addMessage"
                        :min-len="config['minMessageLen'] ?? -1"
                        :max-len="config['maxMessageLen'] ?? -1"
                        :can-send="currentRound && currentRound.canSendMsg"
                        placeholder="Scrivi qui il tuo messaggio per l'assistente."
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped>
@reference '#app.css';

.side-container {
    @apply p-3 bg-pink-650;
}

.choice {
    @apply bg-blue-200 border-2 border-blue-700 flex flex-col items-center justify-center rounded-sm px-4 py-2;
}

.score {
    @apply text-xs font-mono self-center;
}

/* Pulse the Continue CTA so participants don't miss that the phase is over. */
.continue-cta {
    animation: cta-flash 1.1s ease-in-out infinite;
}

@keyframes cta-flash {
    0%, 100% {
        opacity: 1;
        box-shadow: 0 0 0 0 rgba(57, 90, 199, 0.45);
    }
    50% {
        opacity: 0.75;
        box-shadow: 0 0 0 6px rgba(57, 90, 199, 0);
    }
}
</style>